//
// # Abutted Array Utility
//
// Creates abutted segment definitions for transistor arrays.
// When a transistor instance has m > 1, horizontally neighboring
// sub-instances are abutted by default -- edge cells keep outer dummies,
// interior cells have no dummies on either side.
//
// This utility is called from:
// - LayoutInstanceBuilder.create() (new instance with m > 1)
// - LayoutInstanceBuilder.update() (param change on existing array)
// - schematic_to_layout.ts (schematic conversion with m > 1)
//
// Segments are created with placeholder positions (all at origin).
// The caller is responsible for invoking the relative placer with the
// returned constraint edges to compute final positions via outline-based
// abutment.
//

import { Result, Context } from "CoreUtils";
import { schema } from "GenAlphaSchema";
import type { DepotGet } from "Depot";

// Minimal interface -- only needs generateAndCache (not resolve).
// Compatible with both instanceBuilder.GeneratorRegistry and
// schematic_to_layout.GeneratorRegistry.
export interface AbuttedArrayGeneratorRegistry {
  generateAndCache(
    ctx: Context,
    generatorPath: string[],
    params: Record<string, unknown>,
    stackId: schema.StackId,
    depot: DepotGet,
    pdkName?: string
  ): Promise<Result<schema.Ident>>;
  isFet(generatorPath: string[]): boolean;
  // Built by Custom Compiler, which takes none of the abutment params below.
  isExternal(generatorPath: string[]): boolean;
}

// A single relative-placement constraint edge.
// This mirrors the Rust RelativeLocationEdge type from the placer_util crate.
// We duplicate it here because the canonical TypeScript definition lives in
// Tools/src/layout/schema.ts (RelativeConstraints), and GenAlphaModel
// cannot depend on Tools (upward dependency). There is no shared schema
// package that mirrors the Rust tool types to TypeScript yet.
export interface ConstraintEdge {
  source_name: string;
  destination_name: string;
  relation: {
    relation_type: "Left" | "Right" | "Above" | "Below";
    space?: number;
    alignment?: "Start" | "Center" | "End";
  };
}

// Options shared by all abutted-array entry points. Contains everything
// needed to regenerate dummy-stripped variants for a transistor array.
export interface GenerateSegmentTargetsOptions {
  ctx: Context;
  generatorPath: string[];
  baseParams: Record<string, unknown>;
  m: number;
  pdkId: string;
  stackId: schema.StackId;
  generatorRegistry: AbuttedArrayGeneratorRegistry;
  depot: DepotGet;
  // Arrange the m segments as a near-square grid instead of a single row.
  // Unset means a straight line: 1 row x m columns.
  rectangular?: boolean;
}

export interface GenerateSegmentTargetsResult {
  // Target for interior (non-edge) segments. Segments whose entry in
  // `targets` is undefined inherit this value via the instance-level default.
  defaultTarget: schema.Ident;
  // Per-segment target overrides, length m. An entry of undefined means
  // the segment at that index inherits defaultTarget.
  targets: Array<schema.Ident | undefined>;
}

export interface AbuttedSegmentsResult {
  // Segments with per-segment target overrides for edge cells
  segments: Array<schema.layout.Segment>;
  // Interior cell target (used as the instance-level default target)
  defaultTarget: schema.Ident;
  // Constraint edges for the relative placer (horizontal + vertical abutment)
  constraintEdges: Array<ConstraintEdge>;
}

export interface AbuttedSegmentsOptions extends GenerateSegmentTargetsOptions {
  origin: { x: number; y: number };
  // Instance name, used to build segment constraint references (e.g. "N1.0", "N1.1")
  instanceName: string;
}

export interface UpdateAbuttedSegmentsOptions
  extends GenerateSegmentTargetsOptions {
  // The instance's existing segments. Length must equal m. Each segment's
  // xform (loc + reflect) is preserved; only the per-segment target is
  // refreshed from the regenerated dummy-stripped variants.
  existingSegments: Array<schema.layout.Segment>;
}

export interface UpdateAbuttedSegmentsResult {
  segments: Array<schema.layout.Segment>;
  defaultTarget: schema.Ident;
}

/**
 * Recover the arrangement of an already-segmented instance: true if its
 * segments occupy more than one row, false if they sit in a single row.
 *
 * The inverse of `computeGridDimensions` — that derives an arrangement from
 * `(m, rectangular)`, this reads one back off the geometry. It needs no stored
 * flag, so it works for instances built before `rectangular` existed and for
 * callers that have no flag to pass. Undefined when there is nothing to read.
 *
 * It is a reading of the geometry, not a record of intent, and the two can
 * part ways: one hand-moved segment reads as a grid, and segments still
 * sitting at their placeholder origin (a rebuild whose caller never ran the
 * relative placer) read as a single row. Prefer an explicit request when the
 * caller has one.
 *
 * Shared by the layout builder (to preserve an instance's shape across a
 * param edit) and the IDE (to seed the shape control with what the instance
 * actually is), so the two cannot disagree about what shape something has.
 */
export function inferRectangular(
  place: schema.layout.Place | undefined
): boolean | undefined {
  const dims = inferGridDimensions(place);
  return dims === undefined ? undefined : dims.nrows > 1;
}

/**
 * Read back the grid an already-segmented instance is actually laid out on:
 * rows are distinct segment y values, and columns the widest of those rows.
 *
 * Callers deciding whether segments can be refreshed in place need this rather
 * than `inferRectangular`, because agreeing on "is a grid" is not the same as
 * agreeing on the grid. An instance laid out by an older arrangement rule can
 * read as a grid while `computeGridDimensions` now derives different
 * dimensions for the same m — refreshing in place would then hand edge cells
 * to segments that are not on an edge.
 */
export function inferGridDimensions(
  place: schema.layout.Place | undefined
): { ncols: number; nrows: number } | undefined {
  if (!place || place.kind !== "segments") return undefined;
  const rowSizes = new Map<number, number>();
  for (const seg of place.segments) {
    if (seg) rowSizes.set(seg.loc.y, (rowSizes.get(seg.loc.y) ?? 0) + 1);
  }
  if (rowSizes.size === 0) return undefined;
  return { nrows: rowSizes.size, ncols: Math.max(...rowSizes.values()) };
}

/**
 * Compute grid dimensions for m segments.
 *
 * Default is a straight line — 1 row of m columns — which is the conventional
 * multi-finger arrangement. `rectangular` opts into the most balanced exact
 * factorization (m = 12 -> 3x4).
 *
 * A prime m has no factorization but 1 x m, so it stays a single row rather
 * than becoming a ragged grid with a partial last row. A partial row cannot be
 * given correct edge cells by the three variants below: its final segment sits
 * at a column whose reflection parity need not match the one `rightEdgeParams`
 * pre-compensates for, and a partial row of one needs dummies on both sides,
 * which is a fourth and fifth variant. Refusing to make ragged grids removes
 * that whole class rather than patching it.
 */
export function computeGridDimensions(
  m: number,
  rectangular?: boolean
): { ncols: number; nrows: number } {
  if (!rectangular) {
    return { ncols: m, nrows: 1 };
  }
  const sqrt = Math.sqrt(m);
  if (Number.isInteger(sqrt)) {
    return { ncols: sqrt, nrows: sqrt };
  }
  const sqrtFloor = Math.floor(sqrt);
  for (let i = sqrtFloor; i >= 2; i--) {
    if (m % i === 0) {
      return { ncols: m / i, nrows: i };
    }
  }
  // Prime: no exact grid exists, so keep it a single row.
  return { ncols: m, nrows: 1 };
}

/**
 * Generate per-segment target idents for an abutted transistor array.
 *
 * Returns null if the generator is not a transistor (by symbolType) or m <= 1,
 * signaling the caller to fall back to normal segment creation.
 *
 * For ncols > 1, generates 3 cell variants with dummy stripping:
 * - Left-edge:  ndumRight = 0 (keeps left dummies, strips right)
 * - Interior:   ndumLeft = 0, ndumRight = 0 (no dummies)
 * - Right-edge: ndumLeft = 0 (strips left dummies, keeps right)
 *
 * End caps are only placed on the outermost edges of the array --
 * abutting sides never get end caps, mirroring the dummy-removal logic.
 *
 * For ncols === 1, no variant generation is needed -- all segments
 * use the original cell with the user's params as-is. The same holds for a
 * Custom Compiler cell: its PCell has its own dummy and abutment params, not
 * these, so every segment is the one cell the instance's params describe.
 *
 * This function is the shared source of truth for dummy-stripped variant
 * assignment used by both the create and update entry points.
 */
export async function generateSegmentTargets(
  options: GenerateSegmentTargetsOptions
): Promise<Result<GenerateSegmentTargetsResult | null>> {
  const {
    ctx,
    generatorPath,
    baseParams,
    m,
    pdkId,
    stackId,
    generatorRegistry,
    depot,
    rectangular,
  } = options;

  // Only applies to transistor generators with m > 1
  if (!generatorRegistry.isFet(generatorPath) || m <= 1) {
    return Result.Ok(null);
  }

  // Strip m/M from params -- each segment is a single-finger cell.
  // The multiplicity is handled by creating m segments, not by passing m to the generator.
  const {
    m: _m,
    M: _M,
    ...cleanParams
  } = baseParams as Record<string, unknown> & { m?: unknown; M?: unknown };

  const { ncols, nrows } = computeGridDimensions(m, rectangular);

  // For ncols === 1 there is no horizontal abutment -- every segment uses the
  // original cell with the user's params unchanged. Generate it once.
  if (ncols === 1 || generatorRegistry.isExternal(generatorPath)) {
    const result = await generatorRegistry.generateAndCache(
      ctx,
      generatorPath,
      cleanParams,
      stackId,
      depot,
      pdkId
    );
    if (result.err) return Result.Err(result.val);

    return Result.Ok({
      defaultTarget: result.val,
      // All segments inherit defaultTarget; no per-segment override needed
      targets: new Array(m).fill(undefined),
    });
  }

  // ncols > 1: generate 3 dummy-stripped variants.
  // End caps are only placed on the outermost edges of the array --
  // abutting sides never get end caps, mirroring the dummy-removal logic.
  //
  // The right-edge variant accounts for whether it will be horizontally
  // reflected (odd column). When reflected, left/right are swapped, so we
  // generate with left-side params that become right-side after the flip.
  const ndum = (cleanParams.ndum as number) ?? 0;
  const userLeftEndCap = (cleanParams.leftEndCap as boolean) ?? false;
  const userRightEndCap = (cleanParams.rightEndCap as boolean) ?? false;
  const rightEdgeReflected = (ncols - 1) % 2 === 1;

  const leftEdgeParams = {
    ...cleanParams,
    ndumLeft: ndum,
    ndumRight: 0,
    leftEndCap: userLeftEndCap,
    rightEndCap: false,
  };
  const interiorParams = {
    ...cleanParams,
    ndumLeft: 0,
    ndumRight: 0,
    leftEndCap: false,
    rightEndCap: false,
  };
  // When the right edge is on an odd column it gets horiz reflected, so
  // left/right params are swapped: generate with left-side dummies and
  // leftEndCap set to the user's rightEndCap.
  const rightEdgeParams = rightEdgeReflected
    ? {
        ...cleanParams,
        ndumLeft: ndum,
        ndumRight: 0,
        leftEndCap: userRightEndCap,
        rightEndCap: false,
      }
    : {
        ...cleanParams,
        ndumLeft: 0,
        ndumRight: ndum,
        leftEndCap: false,
        rightEndCap: userRightEndCap,
      };

  const [leftResult, interiorResult, rightResult] = await Promise.all([
    generatorRegistry.generateAndCache(
      ctx,
      generatorPath,
      leftEdgeParams,
      stackId,
      depot,
      pdkId
    ),
    generatorRegistry.generateAndCache(
      ctx,
      generatorPath,
      interiorParams,
      stackId,
      depot,
      pdkId
    ),
    generatorRegistry.generateAndCache(
      ctx,
      generatorPath,
      rightEdgeParams,
      stackId,
      depot,
      pdkId
    ),
  ]);

  if (leftResult.err) return Result.Err(leftResult.val);
  if (interiorResult.err) return Result.Err(interiorResult.val);
  if (rightResult.err) return Result.Err(rightResult.val);

  const leftTarget = leftResult.val;
  const interiorTarget = interiorResult.val;
  const rightTarget = rightResult.val;

  // Assign variants by grid role: leftmost column -> leftTarget,
  // rightmost column (or final partial-row segment) -> rightTarget,
  // interior -> undefined (inherits defaultTarget = interiorTarget).
  const targets: Array<schema.Ident | undefined> = new Array(m);
  for (let row = 0; row < nrows; row++) {
    for (let col = 0; col < ncols; col++) {
      const segIndex = row * ncols + col;
      if (segIndex >= m) break; // Partial last row

      const isLeft = col === 0;
      // Every row is full (computeGridDimensions never returns a ragged grid),
      // so the right edge is always the last column.
      const isRight = col === ncols - 1;

      if (isLeft) {
        targets[segIndex] = leftTarget;
      } else if (isRight) {
        targets[segIndex] = rightTarget;
      } else {
        targets[segIndex] = undefined;
      }
    }
  }

  // Interior variant is the default (most segments are interior)
  return Result.Ok({
    defaultTarget: interiorTarget,
    targets,
  });
}

/**
 * Create abutted segments for a new transistor array.
 *
 * Pairs freshly generated per-segment targets (from generateSegmentTargets)
 * with placeholder xforms at `origin` and emits the constraint edges the
 * relative placer needs to abut them. Segment reflect is mirrored on odd
 * columns so adjacent cells share D-D or S-S contacts at the abutment
 * boundary.
 *
 * Returns null if the generator is not a transistor or m <= 1.
 */
export async function createAbuttedSegments(
  options: AbuttedSegmentsOptions
): Promise<Result<AbuttedSegmentsResult | null>> {
  const { m, origin, instanceName, rectangular } = options;

  const targetsResult = await generateSegmentTargets(options);
  if (targetsResult.err) return Result.Err(targetsResult.val);
  if (!targetsResult.val) return Result.Ok(null);

  const { defaultTarget, targets } = targetsResult.val;
  const { ncols, nrows } = computeGridDimensions(m, rectangular);

  const segments: Array<schema.layout.Segment> = [];
  const constraintEdges: Array<ConstraintEdge> = [];

  for (let row = 0; row < nrows; row++) {
    for (let col = 0; col < ncols; col++) {
      const segIndex = row * ncols + col;
      if (segIndex >= m) break; // Partial last row

      // Odd columns get horizontal reflect so adjacent cells share D-D or S-S
      const mirrored = ncols > 1 && col % 2 === 1;
      const target = targets[segIndex];

      const segment: schema.layout.Segment = {
        loc: { x: origin.x, y: origin.y },
        reflect: { horiz: mirrored, vert: false },
        ...(target && { target }),
      };
      segments.push(segment);

      // Horizontal constraint: this segment is Right of the previous column
      if (col > 0) {
        const prevSegIndex = row * ncols + (col - 1);
        constraintEdges.push({
          source_name: `${instanceName}.${segIndex}`,
          destination_name: `${instanceName}.${prevSegIndex}`,
          relation: {
            relation_type: "Right",
            space: 0,
            alignment: "Center",
          },
        });
      }

      // Vertical constraint: this segment is Above the same column in the previous row
      if (row > 0) {
        const aboveSegIndex = (row - 1) * ncols + col;
        constraintEdges.push({
          source_name: `${instanceName}.${segIndex}`,
          destination_name: `${instanceName}.${aboveSegIndex}`,
          relation: {
            relation_type: "Above",
            space: 0,
            alignment: "Center",
          },
        });
      }
    }
  }

  return Result.Ok({
    segments,
    defaultTarget,
    constraintEdges,
  });
}

/**
 * Refresh per-segment targets on an existing transistor array whose params
 * have changed, preserving each segment's xform (loc and reflect).
 *
 * Used by LayoutInstanceBuilder.update() when m is unchanged: the segment
 * count and grid roles are fixed, so we only need to re-point each segment
 * at a freshly regenerated variant under the new params. The relative
 * placer is not invoked -- existing positions carry over verbatim, so no
 * constraint edges are returned.
 *
 * Requires existingSegments.length === m. Returns null if the generator is
 * not a transistor or m <= 1.
 */
export async function updateAbuttedSegments(
  options: UpdateAbuttedSegmentsOptions
): Promise<Result<UpdateAbuttedSegmentsResult | null>> {
  const { existingSegments, m } = options;

  if (existingSegments.length !== m) {
    return Result.Err({
      why: "segment-count-mismatch",
      message: `updateAbuttedSegments requires existingSegments.length (${existingSegments.length}) === m (${m})`,
    });
  }

  const targetsResult = await generateSegmentTargets(options);
  if (targetsResult.err) return Result.Err(targetsResult.val);
  if (!targetsResult.val) return Result.Ok(null);

  const { defaultTarget, targets } = targetsResult.val;

  // Preserve each segment's xform (loc + reflect); overwrite target only.
  // Segments whose new target is undefined inherit defaultTarget -- strip any
  // stale per-segment target from the existing segment in that case.
  const segments: Array<schema.layout.Segment> = existingSegments.map(
    (seg, i) => {
      if (seg === null) return null;
      const newTarget = targets[i];
      if (newTarget === undefined) {
        const { target: _stripped, ...rest } = seg;
        return rest;
      }
      return { ...seg, target: newTarget };
    }
  );

  return Result.Ok({
    segments,
    defaultTarget,
  });
}
